/**
 * EN-837 / EN-860 — decides how <App> should move `routableUserId` (the value the router is keyed
 * on) toward the live Supabase `sessionUserId`, WITHOUT misfiling or silently losing an in-flight
 * recording.
 *
 * Changing `routableUserId` rebuilds the router (`createBrowserRouter`) and remounts the
 * authenticated shell — and with it the MediaRecorder in AssetLibraryProvider inside
 * PermissionWrapper. There is exactly ONE transition that is deferred until the recorder is idle:
 *
 *   - sign-OUT (`id -> null`): the SAME identity is leaving. Deferring keeps the recorder alive so
 *     the browser transport keeps snapshotting to IndexedDB, and there is no OTHER identity to
 *     misfile into — a null session makes `uploadAudio` bail with "no session". Returned as
 *     `deferSwap` for the caller to gate through `runWhenRecorderIdle`.
 *
 * Everything else adopts SYNCHRONOUSLY during render (React's sanctioned derive-state pattern —
 * re-renders with no intervening paint):
 *   - sign-IN from logged-out (`null -> id`): no unauthenticated flash to avoid, no live recorder.
 *   - user SWITCH (`id_A -> id_B`, both non-null, e.g. a cross-tab `SIGNED_IN` as a different user
 *     mid-recording). This is DELIBERATELY not deferred, and it is the crux of the second Bugbot
 *     round. Unlike admin impersonation — which is a header-only change (`X-Effective-User-Id`)
 *     where `supabase.auth.getSession()` still returns the ORIGINAL user, so its whole teardown can
 *     be safely deferred without changing identity — a cross-tab Supabase switch means the session
 *     is ALREADY user-b by the time we observe it. `uploadAudio` derives both the storage path
 *     (`${session.user.id}/…`) and the `Authorization` token from `getSession()` at upload time, so
 *     deferring would keep A's recorder mounted only to upload the clip under B — cross-account
 *     misfiling of A's medical audio. A's recording cannot be persisted as A once A's session is
 *     gone, so the least-bad outcome is to adopt (remount): the browser recorder's periodic
 *     IndexedDB snapshots bound the loss to the last ~15s window and stay recoverable, and no audio
 *     is misfiled into another account.
 *
 * Note: a same-user re-auth / token refresh mints a fresh session object with the SAME id, which is
 * `none` here — no remount. (Keying on the id, not the object, is the round-2 fix.)
 */
export type RoutableUserIdAction =
    | { type: "none" }
    | { type: "adoptSync"; id: string }
    | { type: "deferSwap"; id: null };

export function resolveRoutableUserIdAction(
    sessionUserId: string | null,
    routableUserId: string | null,
): RoutableUserIdAction {
    if (sessionUserId === routableUserId) return { type: "none" };
    // Sign-OUT (same identity leaving) is the only deferred transition — safe to hold because a null
    // session has no other identity to misfile into. See the block comment above.
    if (sessionUserId === null) return { type: "deferSwap", id: null };
    // Sign-IN (null -> id) or a genuine user SWITCH (id_A -> id_B): adopt immediately. A switch is
    // NOT deferrable — the Supabase session is already the new user, so deferring would only upload
    // the prior user's clip under the new identity. See the block comment above.
    return { type: "adoptSync", id: sessionUserId };
}
